import {
  allowedSeasonValues,
  allowedColorFamilyValues,
  allowedColorShadeValues,
  allowedColorToneValues,
  brandOptions,
  organizations,
  suppliers,
  adminUnits,
  contactPersons,
} from './itemsFixtureData';

/**
 * Deterministic fixture data for `items.ts`'s `itemsData`, split into its own file purely to
 * keep `items.ts` under the project's 800-line file cap (`frontend/CLAUDE.md` Code Style) —
 * mirrors the sibling `stylesBulkEditProbeFixtureData.ts` split for the same reason.
 *
 * Testmo 2373062 — "[Items] Duplicate item combination validation" (DL-61919):
 * `auditLogFixtureItem` (audit-log demo row), `duplicateFixtureItemA/B/C` (name+supplier
 * uniqueness probes — A/B share a supplier so renaming B to A's name collides; C has a
 * different supplier so the same rename succeeds).
 *
 * DL-62326 — bulk-edit conditional field visibility:
 * - `bulkEditProbeSupplierEmptyA/B`, `bulkEditProbeSupplierSameA/B`, `bulkEditProbeSupplierDiff` —
 *   Supplier field editable-only-when-empty / Supplier Contact enabled-only-with-shared-supplier
 *   (Testmo 1809237-1809241).
 * - `bulkEditProbeMotherSameA/B` / `bulkEditProbeMotherDiff` — Hierarchy Mother/Child bulk-edit
 *   (Testmo 1809242-1809245), via the `colorFamilyCf` custom-field-entry builder.
 *
 * All isolable together via `?search=BulkEditProbe` (unique substring, absent from every
 * generated/other fixture name) except the duplicate-check trio, isolated via
 * `?search=QA Fixture` — see each spec for its exact navigation.
 */
export const auditLogFixtureItem = {
  id: 90101,
  name: 'Audit Log Fixture Item',
  userDefinedId: 'IT-90101',
  description: 'Original fixture description',
  brand: brandOptions[1],
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 1,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  // Shared non-mixed multiselect CF for dl-59493 bulk-edit demos
  customFields: [
    {
      id: 90101008,
      customFieldId: 8,
      name: 'Season used',
      userDefinedId: null,
      type: 0,
      value: 'AW25',
      itemCustomFieldAllowedValueId: 26,
      customFieldAllowedValueUserDefinedID: null,
      isMandatory: false,
      isSupplierEditable: true,
      internalOnly: false,
      maxChar: null,
      isMultiLine: true,
      allowedDecimals: 0,
      isMultipleAllowedValue: true,
      parentCustomFieldId: null,
      parentCustomFieldName: null,
      allowedValues: allowedSeasonValues,
    },
  ],
};

export const duplicateFixtureItemA = {
  id: 90001,
  name: 'Aluminum Zipper QA Fixture',
  userDefinedId: 'DUP-1001',
  description: null,
  brand: brandOptions[1],
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 10,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
};

export const duplicateFixtureItemB = {
  ...duplicateFixtureItemA,
  id: 90002,
  name: 'Steel Button QA Fixture',
  userDefinedId: 'DUP-1002',
  // Same supplier as Item A (suppliers[0]) — required by the Testmo scenario ("a second item
  // with the same supplier").
};

export const duplicateFixtureItemC = {
  ...duplicateFixtureItemA,
  id: 90003,
  name: 'Bronze Buckle QA Fixture',
  userDefinedId: 'DUP-1003',
  // Different supplier from Item A — the "same name, different supplier saves successfully"
  // adversarial case.
  supplier: suppliers[1],
};

// `supplier: null` requires the array literal below to include at least one null-supplier
// entry from the start so `itemsData`'s inferred element type allows it.
export const bulkEditProbeSupplierEmptyA = {
  id: 90210,
  name: 'BulkEditProbe Supplier Empty A',
  userDefinedId: 'IT-90210',
  description: null,
  brand: brandOptions[0],
  organization: organizations[0],
  supplier: null,
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 1,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
};

export const bulkEditProbeSupplierEmptyB = {
  ...bulkEditProbeSupplierEmptyA,
  id: 90211,
  name: 'BulkEditProbe Supplier Empty B',
  userDefinedId: 'IT-90211',
};

export const bulkEditProbeSupplierSameA = {
  ...bulkEditProbeSupplierEmptyA,
  id: 90212,
  name: 'BulkEditProbe Supplier Same A',
  userDefinedId: 'IT-90212',
  supplier: suppliers[0],
};

export const bulkEditProbeSupplierSameB = {
  ...bulkEditProbeSupplierSameA,
  id: 90213,
  name: 'BulkEditProbe Supplier Same B',
  userDefinedId: 'IT-90213',
};

export const bulkEditProbeSupplierDiff = {
  ...bulkEditProbeSupplierSameA,
  id: 90214,
  name: 'BulkEditProbe Supplier Diff',
  userDefinedId: 'IT-90214',
  // Different supplier from the "Same" pair — drives the mixed-suppliers disabled case.
  supplier: suppliers[1],
};

// Mother/Same A+B share allowed-value 501 ("Warm"); Diff carries 502 ("Cool") — reuses
// `allowedColorFamilyValues`/`allowedColorShadeValues` seeded for DL-61900/DL-61901.
const colorFamilyCf = (cfEntryId: number, allowedValueId: number, label: string) => ({
  id: cfEntryId,
  customFieldId: 14,
  name: 'Color family',
  userDefinedId: null,
  type: 3,
  value: label,
  itemCustomFieldAllowedValueId: allowedValueId,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
  allowedValues: allowedColorFamilyValues,
});

export const bulkEditProbeMotherSameA = {
  ...bulkEditProbeSupplierEmptyA,
  id: 90215,
  name: 'BulkEditProbe Mother Same A',
  userDefinedId: 'IT-90215',
  supplier: suppliers[0],
  customFields: [colorFamilyCf(90215014, 501, 'Warm')],
};

export const bulkEditProbeMotherSameB = {
  ...bulkEditProbeMotherSameA,
  id: 90216,
  name: 'BulkEditProbe Mother Same B',
  userDefinedId: 'IT-90216',
  customFields: [colorFamilyCf(90216014, 501, 'Warm')],
};

export const bulkEditProbeMotherDiff = {
  ...bulkEditProbeMotherSameA,
  id: 90217,
  name: 'BulkEditProbe Mother Diff',
  userDefinedId: 'IT-90217',
  customFields: [colorFamilyCf(90217014, 502, 'Cool')],
};

// Child ("Color shade", customFieldId 15) entry builder, parented to "Color family" (14) — used by
// the dl-58533 hierarchy-validation state-machine specs (DL-65842). The seeded rows below carry an
// EXISTING child value so changing the mother in the modal exercises the orphaned-child paths
// (error / CLEAR / re-select) that the mother-only `bulkEditProbeMother*` rows above cannot reach.
const colorShadeCf = (cfEntryId: number, allowedValueId: number, label: string) => ({
  id: cfEntryId,
  customFieldId: 15,
  name: 'Color shade',
  userDefinedId: null,
  type: 3,
  value: label,
  itemCustomFieldAllowedValueId: allowedValueId,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: 14,
  parentCustomFieldName: 'Color family',
  allowedValues: allowedColorShadeValues,
});

// Both rows share mother "Warm" (501) AND child "Red" (601, a Warm shade), so the child starts
// enabled+valid; changing the mother to "Cool" orphans "Red" and drives the error/CLEAR flow.
export const bulkEditProbeChildWarmA = {
  ...bulkEditProbeMotherSameA,
  id: 90218,
  name: 'BulkEditProbe Child Warm A',
  userDefinedId: 'IT-90218',
  customFields: [colorFamilyCf(90218014, 501, 'Warm'), colorShadeCf(90218015, 601, 'Red')],
};

export const bulkEditProbeChildWarmB = {
  ...bulkEditProbeChildWarmA,
  id: 90219,
  name: 'BulkEditProbe Child Warm B',
  userDefinedId: 'IT-90219',
  customFields: [colorFamilyCf(90219014, 501, 'Warm'), colorShadeCf(90219015, 601, 'Red')],
};

// No mother value at all — pairs with a "Warm" row to make the mothers mixed (one 'Warm', one
// null) for the "mixed incl. one null → child disabled" case (Testmo 753765).
export const bulkEditProbeMotherNull = {
  ...bulkEditProbeMotherSameA,
  id: 90220,
  name: 'BulkEditProbe Mother Null',
  userDefinedId: 'IT-90220',
  customFields: [],
};

// Grandchild ("Color tone", customFieldId 17) entry builder, parented to "Color shade" (15) — the
// THIRD hierarchy level for the dl-58533 part-2 cascade specs (DL-65853).
const colorToneCf = (cfEntryId: number, allowedValueId: number, label: string) => ({
  id: cfEntryId,
  customFieldId: 17,
  name: 'Color tone',
  userDefinedId: null,
  type: 3,
  value: label,
  itemCustomFieldAllowedValueId: allowedValueId,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: null,
  isMultiLine: false,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: 15,
  parentCustomFieldName: 'Color shade',
  allowedValues: allowedColorToneValues,
});

// Two rows sharing all THREE levels — Warm (501) / Red (601) / Crimson (711) — so every level starts
// enabled+valid. Changing Color family (L1) must recursively reset Color shade (L2) AND Color tone
// (L3); changing Color shade (L2) re-filters and resets only Color tone (L3). Drives DL-65853's
// cascade (753759/753768) and grandchild-parent-name messaging (753772).
export const bulkEditProbeThreeLevelA = {
  ...bulkEditProbeMotherSameA,
  id: 90221,
  name: 'BulkEditProbe ThreeLevel A',
  userDefinedId: 'IT-90221',
  customFields: [
    colorFamilyCf(90221014, 501, 'Warm'),
    colorShadeCf(90221015, 601, 'Red'),
    colorToneCf(90221017, 711, 'Crimson'),
  ],
};

export const bulkEditProbeThreeLevelB = {
  ...bulkEditProbeThreeLevelA,
  id: 90222,
  name: 'BulkEditProbe ThreeLevel B',
  userDefinedId: 'IT-90222',
  customFields: [
    colorFamilyCf(90222014, 501, 'Warm'),
    colorShadeCf(90222015, 601, 'Red'),
    colorToneCf(90222017, 711, 'Crimson'),
  ],
};
